import React, { Component } from 'react';
import axios from 'axios';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import Auth from '../../../auth/auth';

// ponytail: self-contained widget (own axios + local state) rather than threading
// per-resource ownership through the data-dictionary reducer — matches the
// user-profile-admin precedent and keeps this droppable on any page.
const API = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  return { 'Accept': 'application/json', 'Content-Type': 'application/json', 'x-access-token': new Auth().getAccessToken() };
}

class OwnershipPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      owners: [],
      derivedOwners: [],
      delegations: [],
      users: [],
      canEdit: false,
      loading: false,
      error: '',
      // Slice 6e — per-section degradation, so one 403 does not blank the panel. See loadAll.
      ownersHidden: false,
      pickersHidden: false,
    };
  }

  componentDidMount() {
    this.loadAll();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.entityId !== this.props.entityId || prevProps.entityType !== this.props.entityType) {
      this.loadAll();
    }
  }

  ownershipType = () => `dd_${this.props.entityType}`;

  // SLICE 6e — SEQUENCED AND PARTIAL-TOLERANT, because enforcing /userProfile turns two of these
  // three calls into real authorization decisions.
  //
  // What this used to be: `Promise.all([loadResource(), loadPickers(), loadPermissions()])` with a
  // single `.catch` that wrote `error` and blanked the panel. Both of the first two calls hit
  // platform_admin-only codes — `GET /ownership/resource/...` carried `access.role:read` and
  // `GET /userProfile/users` carries `user:read`, and NO DATA ROLE HOLDS EITHER. So the first 403
  // rejected the whole Promise.all and error-stated the panel on EVERY data-dictionary details open
  // for the steward, the champion, and the custodian once their direct grant expires 2026-12-13.
  //
  // Two changes, and it is worth being honest about which one carries the weight:
  //
  //   allSettled does the real work. The measured dev population has `isSuperUser` true for 3 of 4
  //   identities, and `canEdit` is `isSuperUser || can_edit` — so the gate below does NOT spare
  //   them, and degrading one section instead of the panel is what keeps the page usable.
  //
  //   The canEdit gate is still correct, for a narrower set than it first appears: only identities
  //   whose incumbent isSuperUser is false AND who hold no data_ownership edit bit (on dev, the
  //   champion alone). You need the user picker only if you can assign an owner, and after this
  //   slice data roles cannot — POST /ownership needs `access:grant`, withheld from data roles by
  //   design. That keeps `user:read` admin-only rather than forcing a widening.
  //
  // `canEdit` itself still comes from the INCUMBENT feature bit plus isSuperUser (:77). Phase 7
  // moves it with the feature-permission tables; until then a narrowed user may still see an edit
  // control the server refuses. Server is authoritative.
  loadAll = () => {
    if (!this.props.entityId) return;
    this.setState({ loading: true, error: '', ownersHidden: false, pickersHidden: false });
    // Permissions first: loadPickers depends on canEdit, so it cannot run in the same batch.
    this.loadPermissions()
      .catch(() => { /* selfRoute — never denies; a failure here is not an access decision */ })
      .then(() => Promise.allSettled([
        this.loadResource(),
        this.state.canEdit ? this.loadPickers() : Promise.resolve(),
      ]))
      .then(([resource, pickers]) => this.setState({
        loading: false,
        // A rejected loader hides ITS OWN section. Rendering a quiet "not visible to you" beats
        // both a blanked panel and an empty list that silently implies "no owners".
        ownersHidden: resource.status === 'rejected',
        pickersHidden: pickers.status === 'rejected',
      }));
  }

  loadResource = () => {
    const { entityId } = this.props;
    // Ownership (and delegation) is schema-level only — nothing to load for objects.
    if (this.props.showOwnership === false) return Promise.resolve();
    return axios.get(`${API}/userProfile/ownership/resource/${this.ownershipType()}/${entityId}`, { headers: authHeaders() })
      .then((ownRes) => {
        const resp = ownRes.data.response || {};
        this.setState({ owners: resp.owners || [], derivedOwners: resp.derivedOwners || [], delegations: resp.delegations || [] });
      });
  }

  loadPickers = () => {
    return axios.get(`${API}/userProfile/users`, { headers: authHeaders() })
      .then((uRes) => {
        this.setState({ users: (uRes.data.response && uRes.data.response.value) || [] });
      });
  }

  loadPermissions = () => {
    return axios.get(`${API}/userProfile/permissions/me`, { headers: authHeaders() }).then((res) => {
      const r = res.data.response || {};
      const perm = (r.permissions || []).find((p) => p.feature_name === 'data_ownership');
      this.setState({ canEdit: Boolean(r.isSuperUser) || Boolean(perm && perm.can_edit) });
    });
  }

  assignOwner = (event, user) => {
    if (!user) return;
    axios.post(`${API}/userProfile/ownership`,
      { ownerType: 'user', ownerId: user.id, resourceType: this.ownershipType(), resourceId: this.props.entityId },
      { headers: authHeaders() }
    ).then(() => this.loadResource()).catch((err) => this.setState({ error: String(err.message || err) }));
  }

  removeOwner = (owner) => {
    axios.delete(`${API}/userProfile/ownership`, {
      headers: authHeaders(),
      data: { ownerType: owner.owner_type, ownerId: owner.owner_id, resourceType: this.ownershipType(), resourceId: this.props.entityId },
    }).then(() => this.loadResource()).catch((err) => this.setState({ error: String(err.message || err) }));
  }

  render() {
    const { owners, derivedOwners, delegations, users, canEdit, error, ownersHidden } = this.state;
    const showOwnership = this.props.showOwnership !== false;

    return (
      <div className="ownership-panel" style={{ marginTop: 12 }} data-testid="dd-ownership-panel" data-entity-id={this.props.entityId}>
        {showOwnership && (
        <div className="panel-section">
          <label className="panel-label">Access Owner <span style={{ color: '#9e9e9e', fontWeight: 400 }}>(controls visibility &amp; delegation)</span></label>
          <div>
            {/* Slice 6e — "not visible to you" is NOT the same claim as "Not assigned", and
                conflating them would have this panel assert there is no owner when the truth is
                that the reader may not see who it is. */}
            {ownersHidden && (
              <span style={{ color: '#9e9e9e', fontSize: 13 }} data-testid="ownership-panel-partial">
                Owner details are not visible to you
              </span>
            )}
            {!ownersHidden && owners.length === 0 && derivedOwners.length === 0 && <span style={{ color: '#9e9e9e', fontSize: 13 }}>Not assigned</span>}
            {owners.map((o) => (
              <Chip
                key={`${o.owner_type}-${o.owner_id}`}
                label={`${o.owner_name || o.owner_id}${o.owner_email ? ` (${o.owner_email})` : ''}`}
                size="small"
                style={{ margin: 2 }}
                onDelete={canEdit ? () => this.removeOwner(o) : undefined}
              />
            ))}
            {derivedOwners.map((d) => (
              <Chip
                key={`derived-${d.userId}`}
                label={`${d.name || d.userId}${d.email ? ` (${d.email})` : ''} · ${d.source}`}
                size="small"
                variant="outlined"
                title={`Auto-mapped from ${d.source} on a table in this schema`}
                style={{ margin: 2, borderStyle: 'dashed', color: '#616161' }}
              />
            ))}
          </div>
          {derivedOwners.length > 0 && (
            <div style={{ color: '#9e9e9e', fontSize: 11, marginTop: 4 }}>
              Dashed entries are auto-mapped from a table&apos;s Business/Tech Owner and can&apos;t be removed here.
            </div>
          )}
          {canEdit && (
            <Autocomplete
              options={users}
              getOptionLabel={(u) => `${u.name}${u.email ? ` (${u.email})` : ''}`}
              onChange={this.assignOwner}
              value={null}
              blurOnSelect
              renderInput={(params) => <TextField {...params} variant="standard" placeholder="Assign an owner..." inputProps={{...params.inputProps, 'data-testid': 'dd-ownership-assign-input'}} />}
            />
          )}
        </div>
        )}

        {showOwnership && delegations && delegations.length > 0 && (
          <div className="panel-section" style={{ marginTop: 8 }}>
            {delegations.map((d) => (
              <Chip
                key={d.id}
                size="small"
                style={{ margin: 2, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                label={`Delegated to ${d.delegatee_name || d.delegatee_user_id} until ${new Date(d.expires_at).toLocaleDateString()}`}
              />
            ))}
          </div>
        )}

        {error && <div style={{ color: '#c62828', fontSize: 12, marginTop: 8 }}>{error}</div>}
      </div>
    );
  }
}

export default OwnershipPanel;
